﻿import React from 'react';
import { motion } from 'motion/react';
import { Shield, Lock, FileText, Database, Radio, Cpu, Smartphone, Network, Globe, ArrowLeft } from 'lucide-react';
import { toast } from 'sonner'; // Bug 30: 添加 toast 导入

export function FoundersSanctuary({ t, user, onBack }: { t: any; user: any; onBack: () => void }) {
  const blueprints = [
    {
      id: 'SHARD_01',
      title: t.neuralShardingProtocol || '神经分片协议',
      description: t.neuralShardingProtocolDesc || '在不影响推理速度的情况下将大语言模型权重拆分到边缘设备的核心算法。',
      status: t.blueprintStatusVerified || '已验证',
      icon: <Network size={24} />
    },
    {
      id: 'MESH_VOID',
      title: t.zeroKnowledgeMesh || '零知识网格',
      description: t.zeroKnowledgeMeshDesc || '加密通信层确保任何对等节点都无法看到另一节点的原始感官输入。',
      status: t.blueprintStatusEncrypted || '已加密',
      icon: <Shield size={24} />
    },
    {
      id: 'KINETIC_K',
      title: t.kineticKernelV2 || '动态内核 v2',
      description: t.kineticKernelV2Desc || '旨在通过亚阈值神经脉冲将硅算力与人类直觉连接的的操作系统架构。',
      status: t.blueprintStatusStable || '稳定',
      icon: <Cpu size={24} />
    }
  ];

  return (
    <div className="space-y-24 py-12">
      {/* Back Button */}
      <motion.button
        initial={{ opacity: 0, x: -20 }}
        animate={{ opacity: 1, x: 0 }}
        onClick={onBack}
        className="flex items-center gap-2 text-white/40 hover:text-white transition-colors group"
      >
        <ArrowLeft size={16} className="group-hover:-translate-x-1 transition-transform" />
        <span className="text-xs font-black uppercase tracking-widest">{t.returnToHub || '返回中心'}</span>
      </motion.button>

      {/* Header Section */}
      <section className="text-center space-y-8">
        <motion.div
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          className="inline-flex items-center gap-3 px-6 py-2 bg-white/5 border border-white/10 rounded-full"
        >
          <Lock size={14} className="text-celestial-saturn" />
          <span className="text-xs font-black uppercase tracking-[0.4em] text-white/60">{t.restrictedAccess || '受限访问 // 圣所等级-7'}</span>
        </motion.div>

        <div className="space-y-4">
          <h1 className="text-7xl font-black tracking-tighter leading-none italic uppercase italic">
            {t.founderVision || "Founder's"} <br /> <span className="text-celestial-saturn text-glow">{t.foundersSanctuary || '圣所'}</span>
          </h1>
          <p className="text-xl text-white/55 max-w-2xl mx-auto italic">
            {t.sanctuaryHeroDesc || 'The mathematical foundation of 小枫. These archives contain the blueprints for the sovereign neural future.'}
          </p>
        </div>
      </section>

      {/* Blueprint Grid */}
      <section className="grid lg:grid-cols-3 gap-8">
        {blueprints.map((item, i) => (
          <motion.div
            key={item.id}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: i * 0.2 }}
            className="p-8 glass-dark rounded-[3rem] border border-white/5 hover:border-white/20 transition-all group overflow-hidden relative"
          >
            <div className="absolute -right-4 -top-4 opacity-5 group-hover:opacity-10 transition-opacity">
               {item.icon}
            </div>

            <div className="space-y-6 relative z-10">
               <div className="text-xs font-mono text-celestial-saturn tracking-[0.3em] font-bold">
                 {t.moduleIdLabel || '模块 ID：'} {item.id}
               </div>

               <h3 className="text-2xl font-black tracking-tight text-white/90 uppercase">{item.title}</h3>
               <p className="text-sm text-white/40 leading-relaxed italic">{item.description}</p>

               <div className="pt-6 flex justify-between items-center border-t border-white/5">
                  <div className="flex items-center gap-2">
                    <div className="w-1.5 h-1.5 rounded-full bg-celestial-saturn animate-pulse" />
                    <span className="text-xs font-black text-white/40 uppercase">{item.status}</span>
                  </div>
                  <button onClick={() => toast.info(t.archiveComingSoon || '档案功能开发中，敬请期待')} className="text-xs font-black text-white/45 hover:text-white transition-colors uppercase tracking-widest border border-white/10 px-4 py-2 rounded-full">
                    {t.openArchive || '打开档案'}
                  </button>
               </div>
            </div>
          </motion.div>
        ))}
      </section>

      {/* The Protocol Codex */}
      <section className="glass p-16 rounded-[4rem] border border-white/5 relative overflow-hidden">
         <div className="absolute top-0 right-0 p-12 text-[120px] font-black text-white/25 leading-none select-none pointer-events-none">
           {t.codexWatermark || '密码集'}
         </div>

         <div className="max-w-3xl space-y-12 relative z-10">
            <div className="space-y-4">
              <h2 className="text-4xl font-black italic uppercase tracking-tighter">{t.theShard || '碎片'} <span className="text-celestial-saturn">{t.manifesto || '宣言'}</span></h2>
              <div className="w-20 h-1 bg-celestial-saturn" />
            </div>

            <div className="grid gap-8 font-mono text-xs leading-loose text-white/60 italic">
               <div className="space-y-2 border-l-2 border-white/5 pl-8">
                 <p className="text-white/80 font-bold uppercase">{t.manifestoSection01Title || '0x01: Distribution of Intelligence'}</p>
                 <p>{t.manifestoSection01Body || 'Intelligence shall not be centralized. 小枫 nodes sharding ensures that even the founding architects cannot access the collective memory of the mesh. Sovereignty is non-negotiable.'}</p>
               </div>
               <div className="space-y-2 border-l-2 border-white/5 pl-8">
                 <p className="text-white/80 font-bold uppercase">{t.manifestoSection02Title || '0x02: Kinetic Interface Theory'}</p>
                 <p>{t.manifestoSection02Body || '操作系统是神经系统的延伸。每次窗口缩放、每次焦点切换，都是映射到硅上的神经冲动。机器必须以思想般的速度响应。'}</p>
               </div>
               <div className="space-y-2 border-l-2 border-white/5 pl-8">
                 <p className="text-white/80 font-bold uppercase">{t.manifestoSection03Title || '0x03: Persistent Shards'}</p>
                 <p>{t.manifestoSection03Body || '数据以短暂碎片存在。节点可以离开网格，但他对集体推理的贡献在持久分片层保持加密。'}</p>
               </div>
            </div>
         </div>
      </section>
    </div>
  );
}
